import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { useUIStore, type Theme } from '@/core/stores/ui.store';

interface ThemeOptionProps {
  value: Theme;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onSelect: (value: Theme) => void;
}

function ThemeOption({ value, label, icon, isActive, onSelect }: ThemeOptionProps) {
  return (
    <button
      type="button"
      data-testid={`settings-theme-option-${value}`}
      onClick={() => onSelect(value)}
      className={cn(
        'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors w-full',
        isActive
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {icon}
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

interface ThemeSectionProps {
  showHeader?: boolean;
}

export function ThemeSection({ showHeader = true }: ThemeSectionProps) {
  const { t } = useTranslation('settings');
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);

  return (
    <div className="space-y-4" data-testid="settings-theme-section">
      {showHeader && (
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('theme.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('theme.description')}</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <ThemeOption
          value="light"
          label={t('theme.light')}
          icon={<Sun className="w-5 h-5" />}
          isActive={theme === 'light'}
          onSelect={setTheme}
        />
        <ThemeOption
          value="dark"
          label={t('theme.dark')}
          icon={<Moon className="w-5 h-5" />}
          isActive={theme === 'dark'}
          onSelect={setTheme}
        />
        <ThemeOption
          value="system"
          label={t('theme.system')}
          icon={<Monitor className="w-5 h-5" />}
          isActive={theme === 'system'}
          onSelect={setTheme}
        />
      </div>
    </div>
  );
}
